import {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import dayjs, { type Dayjs } from 'dayjs';
import { ChevronLeft, ChevronRight, Clock } from 'lucide-react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface PickerBaseProps {
    label?: ReactNode;
    value: unknown;
    onChange?: (value: Dayjs | null) => void;
    disabled?: boolean;
    required?: boolean;
    className?: string;
}

interface CalendarDay {
    date: Dayjs;
    isCurrentMonth: boolean;
    isToday: boolean;
}

interface PopupPosition {
    top: number;
    left: number;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WEEKDAY_LABELS = ['日', '一', '二', '三', '四', '五', '六'];
const MINUTE_STEP = 5;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function toDayjs(value: unknown): Dayjs | null {
    if (!value) return null;
    const d = dayjs(value as dayjs.ConfigType);
    return d.isValid() ? d : null;
}

function generateCalendarDays(year: number, month: number): CalendarDay[] {
    const firstDay = dayjs().year(year).month(month).startOf('month');
    const startOfWeek = firstDay.startOf('week');
    const today = dayjs().startOf('day');

    const days: CalendarDay[] = [];
    const TOTAL_CELLS = 42;
    for (let i = 0; i < TOTAL_CELLS; i++) {
        const date = startOfWeek.add(i, 'day');
        days.push({
            date,
            isCurrentMonth: date.month() === month,
            isToday: date.isSame(today, 'day'),
        });
    }
    return days;
}

function generateHourOptions(): number[] {
    return Array.from({ length: 24 }, (_, i) => i);
}

function generateMinuteOptions(): number[] {
    return Array.from({ length: 60 / MINUTE_STEP }, (_, i) => i * MINUTE_STEP);
}

function padTwo(n: number): string {
    return String(n).padStart(2, '0');
}

// ---------------------------------------------------------------------------
// usePickerPopup — portal-based popup that escapes overflow:hidden ancestors
// ---------------------------------------------------------------------------

function usePickerPopup(triggerRef: React.RefObject<HTMLElement | null>) {
    const popupRef = useRef<HTMLDivElement>(null);
    const [position, setPosition] = useState<PopupPosition>({ top: 0, left: 0 });

    // Recalculate position when popup mounts or window scrolls/resizes
    const updatePosition = useCallback(() => {
        if (!triggerRef.current) return;
        const rect = triggerRef.current.getBoundingClientRect();
        setPosition({
            top: rect.bottom + 4,
            left: rect.left,
        });
    }, [triggerRef]);

    useLayoutEffect(() => {
        updatePosition();
    }, [updatePosition]);

    useEffect(() => {
        window.addEventListener('scroll', updatePosition, true);
        window.addEventListener('resize', updatePosition);
        return () => {
            window.removeEventListener('scroll', updatePosition, true);
            window.removeEventListener('resize', updatePosition);
        };
    }, [updatePosition]);

    return { popupRef, position };
}

function useClickOutside(refs: React.RefObject<HTMLElement | null>[], onClose: () => void) {
    useEffect(() => {
        function handleClick(e: MouseEvent) {
            const clickedInside = refs.some((ref) => ref.current?.contains(e.target as Node));
            if (!clickedInside) onClose();
        }
        function handleKeyDown(e: KeyboardEvent) {
            if (e.key === 'Escape') onClose();
        }
        document.addEventListener('mousedown', handleClick);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [refs, onClose]);
}

// ---------------------------------------------------------------------------
// usePortalContainer — find the best container for portal rendering.
// If inside a <dialog> (top-layer), portal into the dialog so the popup
// isn't hidden beneath it. Otherwise fall back to document.body.
// ---------------------------------------------------------------------------

function usePortalContainer(triggerRef: React.RefObject<HTMLElement | null>): HTMLElement {
    const [container, setContainer] = useState<HTMLElement>(document.body);

    useEffect(() => {
        const dialog = triggerRef.current?.closest('dialog');
        setContainer(dialog ?? document.body);
    }, [triggerRef]);

    return container;
}

// ---------------------------------------------------------------------------
// PickerPortal — renders popup content via portal
// ---------------------------------------------------------------------------

function PickerPortal({
    position,
    popupRef,
    container,
    children,
}: {
    position: PopupPosition;
    popupRef: React.RefObject<HTMLDivElement | null>;
    container: HTMLElement;
    children: ReactNode;
}) {
    return createPortal(
        <div
            ref={popupRef}
            className="fixed z-[1100] rounded-box border border-base-300 bg-base-100 p-3 shadow-lg"
            style={{ top: position.top, left: position.left }}
        >
            {children}
        </div>,
        container
    );
}

// ---------------------------------------------------------------------------
// CalendarGrid (shared sub-component)
// ---------------------------------------------------------------------------

function CalendarGrid({
    viewYear,
    viewMonth,
    selectedDate,
    onSelectDate,
    onPrevMonth,
    onNextMonth,
}: {
    viewYear: number;
    viewMonth: number;
    selectedDate: Dayjs | null;
    onSelectDate: (date: Dayjs) => void;
    onPrevMonth: () => void;
    onNextMonth: () => void;
}) {
    const days = useMemo(() => generateCalendarDays(viewYear, viewMonth), [viewYear, viewMonth]);
    const monthLabel = dayjs().year(viewYear).month(viewMonth).format('YYYY 年 M 月');

    return (
        <div className="w-64">
            {/* Month navigation */}
            <div className="mb-2 flex items-center justify-between">
                <button
                    type="button"
                    className="btn btn-ghost btn-circle btn-sm"
                    onClick={onPrevMonth}
                >
                    <ChevronLeft className="h-4 w-4" />
                </button>
                <span className="text-sm font-semibold">{monthLabel}</span>
                <button
                    type="button"
                    className="btn btn-ghost btn-circle btn-sm"
                    onClick={onNextMonth}
                >
                    <ChevronRight className="h-4 w-4" />
                </button>
            </div>

            {/* Weekday headers */}
            <div className="grid grid-cols-7 gap-0 text-center">
                {WEEKDAY_LABELS.map((label) => (
                    <div key={label} className="pb-1 text-xs font-medium text-base-content/50">
                        {label}
                    </div>
                ))}
            </div>

            {/* Day cells */}
            <div className="grid grid-cols-7 gap-0">
                {days.map((day) => {
                    const isSelected = selectedDate?.isSame(day.date, 'day') ?? false;
                    return (
                        <button
                            type="button"
                            key={day.date.format('YYYY-MM-DD')}
                            className={[
                                'btn btn-ghost btn-sm btn-square text-xs',
                                !day.isCurrentMonth && 'opacity-30',
                                day.isToday && !isSelected && 'border border-primary text-primary',
                                isSelected && 'btn-primary',
                            ]
                                .filter(Boolean)
                                .join(' ')}
                            onClick={() => onSelectDate(day.date)}
                        >
                            {day.date.date()}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

// ---------------------------------------------------------------------------
// TimeSelector (shared sub-component)
// ---------------------------------------------------------------------------

function TimeSelector({
    hour,
    minute,
    onChangeHour,
    onChangeMinute,
}: {
    hour: number;
    minute: number;
    onChangeHour: (h: number) => void;
    onChangeMinute: (m: number) => void;
}) {
    const hours = useMemo(generateHourOptions, []);
    const minutes = useMemo(generateMinuteOptions, []);

    return (
        <div className="flex items-center justify-center gap-2">
            <Clock className="h-4 w-4 text-base-content/50" />
            <select
                className="select select-bordered select-sm w-20"
                value={hour}
                onChange={(e) => onChangeHour(Number(e.target.value))}
            >
                {hours.map((h) => (
                    <option key={h} value={h}>
                        {padTwo(h)}
                    </option>
                ))}
            </select>
            <span className="text-base-content/60 font-bold">:</span>
            <select
                className="select select-bordered select-sm w-20"
                value={minute}
                onChange={(e) => onChangeMinute(Number(e.target.value))}
            >
                {minutes.map((m) => (
                    <option key={m} value={m}>
                        {padTwo(m)}
                    </option>
                ))}
            </select>
        </div>
    );
}

// ---------------------------------------------------------------------------
// DatePicker
// ---------------------------------------------------------------------------

export function DatePicker({
    label,
    value,
    onChange,
    disabled,
    required,
    className,
}: PickerBaseProps) {
    const parsed = toDayjs(value);
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => (parsed ?? dayjs()).year());
    const [viewMonth, setViewMonth] = useState(() => (parsed ?? dayjs()).month());
    const containerRef = useRef<HTMLDivElement>(null);
    const { popupRef, position } = usePickerPopup(containerRef);
    const portalContainer = usePortalContainer(containerRef);

    const close = useCallback(() => setIsOpen(false), []);
    useClickOutside([containerRef, popupRef], close);

    useEffect(() => {
        if (parsed) {
            setViewYear(parsed.year());
            setViewMonth(parsed.month());
        }
    }, [parsed?.format('YYYY-MM')]);

    const handleSelectDate = (date: Dayjs) => {
        onChange?.(date);
        setIsOpen(false);
    };

    const handlePrevMonth = () => {
        const prev = dayjs().year(viewYear).month(viewMonth).subtract(1, 'month');
        setViewYear(prev.year());
        setViewMonth(prev.month());
    };

    const handleNextMonth = () => {
        const next = dayjs().year(viewYear).month(viewMonth).add(1, 'month');
        setViewYear(next.year());
        setViewMonth(next.month());
    };

    const displayValue = parsed ? parsed.format('YYYY-MM-DD') : '';

    return (
        <div ref={containerRef} className={`relative w-full ${className ?? ''}`}>
            <label className="floating-label w-full">
                <input
                    className="input w-full cursor-pointer"
                    type="text"
                    readOnly
                    placeholder={typeof label === 'string' ? label : ' '}
                    value={displayValue}
                    disabled={disabled}
                    required={required}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                />
                {label ? <span>{label}</span> : null}
            </label>

            {isOpen && (
                <PickerPortal position={position} popupRef={popupRef} container={portalContainer}>
                    <CalendarGrid
                        viewYear={viewYear}
                        viewMonth={viewMonth}
                        selectedDate={parsed}
                        onSelectDate={handleSelectDate}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                    />
                </PickerPortal>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// TimePicker
// ---------------------------------------------------------------------------

export function TimePicker({
    label,
    value,
    onChange,
    disabled,
    required,
    className,
}: PickerBaseProps) {
    const parsed = toDayjs(value);
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement>(null);
    const { popupRef, position } = usePickerPopup(containerRef);
    const portalContainer = usePortalContainer(containerRef);

    const close = useCallback(() => setIsOpen(false), []);
    useClickOutside([containerRef, popupRef], close);

    const currentHour = parsed?.hour() ?? 0;
    const currentMinute = parsed?.minute() ?? 0;
    const roundedMinute = Math.round(currentMinute / MINUTE_STEP) * MINUTE_STEP;

    const handleChangeHour = (h: number) => {
        const base = parsed ?? dayjs().startOf('day');
        onChange?.(base.hour(h).minute(roundedMinute));
    };

    const handleChangeMinute = (m: number) => {
        const base = parsed ?? dayjs().startOf('day');
        onChange?.(base.hour(currentHour).minute(m));
    };

    const displayValue = parsed ? parsed.format('HH:mm') : '';

    return (
        <div ref={containerRef} className={`relative w-full ${className ?? ''}`}>
            <label className="floating-label w-full">
                <input
                    className="input w-full cursor-pointer"
                    type="text"
                    readOnly
                    placeholder={typeof label === 'string' ? label : ' '}
                    value={displayValue}
                    disabled={disabled}
                    required={required}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                />
                {label ? <span>{label}</span> : null}
            </label>

            {isOpen && (
                <PickerPortal position={position} popupRef={popupRef} container={portalContainer}>
                    <TimeSelector
                        hour={currentHour}
                        minute={roundedMinute}
                        onChangeHour={handleChangeHour}
                        onChangeMinute={handleChangeMinute}
                    />
                </PickerPortal>
            )}
        </div>
    );
}

// ---------------------------------------------------------------------------
// DateTimePicker
// ---------------------------------------------------------------------------

export function DateTimePicker({
    label,
    value,
    onChange,
    disabled,
    required,
    className,
}: PickerBaseProps) {
    const parsed = toDayjs(value);
    const [isOpen, setIsOpen] = useState(false);
    const [viewYear, setViewYear] = useState(() => (parsed ?? dayjs()).year());
    const [viewMonth, setViewMonth] = useState(() => (parsed ?? dayjs()).month());
    const containerRef = useRef<HTMLDivElement>(null);
    const { popupRef, position } = usePickerPopup(containerRef);
    const portalContainer = usePortalContainer(containerRef);

    const close = useCallback(() => setIsOpen(false), []);
    useClickOutside([containerRef, popupRef], close);

    useEffect(() => {
        if (parsed) {
            setViewYear(parsed.year());
            setViewMonth(parsed.month());
        }
    }, [parsed?.format('YYYY-MM')]);

    const currentHour = parsed?.hour() ?? 0;
    const currentMinute = parsed?.minute() ?? 0;
    const roundedMinute = Math.round(currentMinute / MINUTE_STEP) * MINUTE_STEP;

    const handleSelectDate = (date: Dayjs) => {
        const merged = date.hour(currentHour).minute(roundedMinute);
        onChange?.(merged);
    };

    const handleChangeHour = (h: number) => {
        const base = parsed ?? dayjs().startOf('day');
        onChange?.(base.hour(h).minute(roundedMinute));
    };

    const handleChangeMinute = (m: number) => {
        const base = parsed ?? dayjs().startOf('day');
        onChange?.(base.hour(currentHour).minute(m));
    };

    const handlePrevMonth = () => {
        const prev = dayjs().year(viewYear).month(viewMonth).subtract(1, 'month');
        setViewYear(prev.year());
        setViewMonth(prev.month());
    };

    const handleNextMonth = () => {
        const next = dayjs().year(viewYear).month(viewMonth).add(1, 'month');
        setViewYear(next.year());
        setViewMonth(next.month());
    };

    const displayValue = parsed ? parsed.format('YYYY-MM-DD HH:mm') : '';

    return (
        <div ref={containerRef} className={`relative w-full ${className ?? ''}`}>
            <label className="floating-label w-full">
                <input
                    className="input w-full cursor-pointer"
                    type="text"
                    readOnly
                    placeholder={typeof label === 'string' ? label : ' '}
                    value={displayValue}
                    disabled={disabled}
                    required={required}
                    onClick={() => !disabled && setIsOpen(!isOpen)}
                />
                {label ? <span>{label}</span> : null}
            </label>

            {isOpen && (
                <PickerPortal position={position} popupRef={popupRef} container={portalContainer}>
                    <CalendarGrid
                        viewYear={viewYear}
                        viewMonth={viewMonth}
                        selectedDate={parsed}
                        onSelectDate={handleSelectDate}
                        onPrevMonth={handlePrevMonth}
                        onNextMonth={handleNextMonth}
                    />
                    <div className="divider my-1" />
                    <TimeSelector
                        hour={currentHour}
                        minute={roundedMinute}
                        onChangeHour={handleChangeHour}
                        onChangeMinute={handleChangeMinute}
                    />
                </PickerPortal>
            )}
        </div>
    );
}
